<template>
  <v-container>
    <!-- Address Dialog -->
    <v-dialog v-model="dialog" width="500">
      <v-card>
        <v-card-title>
          <span class="primary--text"> Search Results </span>
          <v-spacer />
          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-5">
          <section v-if="lookupResults.data">
            <p class="mb-1"><b>Address:</b> {{ lookupResults.data.address }}</p>
            <p class="mb-1"><b>Latitude:</b> {{ lookupResults.data.lat }}</p>
            <p class="mb-3"><b>Longitude:</b> {{ lookupResults.data.lng }}</p>
          </section>
          <v-alert v-if="lookupResults.message" :value="true" type="warning">{{
            lookupResults.message
          }}</v-alert>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="text-capitalize"
            v-if="isGoogleAPIAvailable"
            depressed
            @click="bringSearchResultsToMap"
          >
            Fine Tune on Map
          </v-btn>
          <v-btn
            class="text-capitalize"
            color="primary"
            depressed
            @click="acceptSearchResults"
          >
            Accept Search Results
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Location Picker Dialog -->
    <v-dialog v-model="locationPickerDialog" width="700">
      <v-card>
        <v-card-title>
          <span class="primary--text"> Find Location </span>
          <v-spacer />
          <v-btn icon @click="locationPickerDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text>
          <v-sheet class="grey lighten-3 mt-2 pa-4 rounded-lg">
            <p class="font-weight-bold text-body-1">Geolocation Selectors</p>
            <p class="text-body-2">
              Use the Google Map Picker <b>OR</b> the Search Address input field
              to find the coordinates of the address stipulated by your company
              policy
            </p>
          </v-sheet>
          <div class="mt-5">
            <v-tabs v-model="activeTab">
              <v-tab class="text-capitalize" :disabled="!isGoogleAPIAvailable">
                Google Map Picker
              </v-tab>
              <v-tab class="text-capitalize"> Search Address </v-tab>

              <v-tab-item :disabled="!isGoogleAPIAvailable">
                <section class="py-4">
                  <!-- Google Map Here -->
                  <google-map-picker
                    :apiKey="yapuSettings.googleAPIKey"
                    :coordinates="coordinates"
                    @updated="(e) => (coordinates = e)"
                  />
                </section>
              </v-tab-item>

              <v-tab-item class="px-5">
                <v-text-field
                  dense
                  outlined
                  style="margin-bottom: 40px; margin-top: 40px"
                  label="Enter address here then click the search icon."
                  v-model="lookupAddress"
                  :loading="lookupInProgress"
                  hint="eg. street, city, state/parish, country"
                  persistent-hint
                  append-outer-icon="mdi-magnify"
                  @click:append-outer="searchGeocodingAPI"
                ></v-text-field>
              </v-tab-item>
            </v-tabs>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            depressed
            @click="locationPickerDialog = false"
          >
            Finish
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row justify="space-between">
      <v-col cols="12" sm="6">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-card outlined class="py-md-4 pa-3 px-4">
            <v-row dense align="center" class="mb-2" justify="space-between">
              <v-col cols="12" sm="6">
                <span class="font-weight-bold">Location</span>
              </v-col>
              <v-col cols="12" sm="6" class="text-sm-right">
                <v-btn
                  depressed
                  color="primary"
                  small
                  height="36"
                  class="text-capitalize font-weight-bold"
                  outlined
                  @click="openLocationPicker"
                >
                  <v-icon class="mr-2" small>mdi-map-marker</v-icon>
                  Select Location
                </v-btn>
              </v-col>
            </v-row>
            <p class="text-body-2">
              You can enter the coordinates manually <b>OR</b> use the button
              above.
            </p>
            <v-row align="center" justify="space-between" class="mt-2">
              <v-col cols="6" class="py-0">
                <v-text-field
                  outlined
                  dense
                  v-model="coordinates.lat"
                  type="number"
                  label="Latitude*"
                  :rules="latitudeRules"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="6" class="py-0">
                <v-text-field
                  v-model="coordinates.lng"
                  type="number"
                  label="Longitude*"
                  :rules="longitudeRules"
                  required
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card>

          <v-row align="center" justify="space-between" class="mt-5">
            <v-col cols="6" class="pt-0">
              <v-select
                v-model="questions.selectedHomeType"
                item-value="name"
                item-text="name"
                :items="homeTypes"
                label="Home Type*"
                :rules="requireRule"
                required
                outlined
                dense
              />
            </v-col>
            <v-col cols="6" class="pt-0 px-2">
              <v-select
                v-model="questions.selectedBusinessType"
                label="Business Type*"
                item-value="name"
                item-text="name"
                :items="businessTypes"
                :rules="requireRule"
                required
                outlined
                dense
              />
            </v-col>
          </v-row>

          <h3>Exposure</h3>
          <p class="text--secondary">
            Please select the exposure details below.
          </p>
          <v-row class="mt-2">
            <v-col
              cols="6"
              v-for="item in questions.exposure"
              :key="item.keyName"
              class="pb-0 px-2"
            >
              <v-select
                v-model="item.selected"
                item-value="name"
                item-text="name"
                :label="item.type + '*'"
                :items="item.details.filter((el) => el.required)"
                :rules="requireRule"
                required
                dense
                outlined
              />
            </v-col>
          </v-row>

          <h3 class="mt-2">Sensitivity</h3>
          <p>Please select the sensitivity for each category below.</p>
          <v-row row wrap>
            <v-col
              v-for="item in questions.sensitivity"
              :key="item.keyName"
              cols="12"
              class="d-flex pb-0 justify-space-between"
            >
              <v-select
                v-model="item.selectedSensitivityType"
                item-value="name"
                item-text="name"
                :label="item.name + ' Type*'"
                :items="item.sensitivityType.filter((el) => el.required)"
                :rules="requireRule"
                required
                dense
                outlined
                class="mr-2"
              />

              <v-select
                v-model="item.selectedMaterial"
                item-value="name"
                item-text="name"
                :label="item.name + ' Material*'"
                :items="item.materials.filter((el) => el.required)"
                :rules="requireRule"
                required
                dense
                outlined
                class="ml-2"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-col>

      <v-col cols="12" sm="6">
        <h3>Assessment Questions</h3>
        <p class="text--secondary">Please complete the questionnaire below</p>
        <v-divider />
        <v-form ref="questionForm" v-model="questionFormValid" lazy-validation>
          <v-list
            style="max-height: calc(100vh - 350px); overflow-y: auto"
            id="adaptiveGroup"
            three-line
          >
            <v-list-group
              v-for="(item, index) in questions.adaptiveCapacity"
              :key="item.keyName"
              v-model="item.active"
              no-action
              style="border-bottom: 1px dashed #ccc"
            >
              <template v-slot:activator>
                <v-list-item class="py-2" :set="(len = item.questions.length)">
                  <v-list-item-avatar
                    v-if="
                      countCompletedQuestions(item.questions) !== len &&
                      adaptiveCapacityErrors[item.keyName]
                    "
                    color="grey lighten-4"
                  >
                    <v-icon color="red">mdi-alert</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title
                      class="font-weight-bold title grey--text text--darken-2"
                      >{{ index + 1 }}. {{ item.type }}</v-list-item-title
                    >
                    <v-list-item-subtitle>
                      Completed
                      {{ countCompletedQuestions(item.questions) }}/{{ len }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle
                      class="text-sm red--text"
                      v-if="
                        countCompletedQuestions(item.questions) !== len &&
                        adaptiveCapacityErrors[item.keyName]
                      "
                    >
                      Please complete questions!
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>

              <v-list-item
                v-for="question in item.questions"
                :key="question.keyName"
                class="pl-10 text-wrap"
              >
                <v-list-item-content class="pl-0 text-wrap">
                  <v-list-item-title class="question">{{
                    toSentenceCase(question.name)
                  }}</v-list-item-title>
                  <v-list-item-subtitle style="padding-left: 10px !important">
                    <v-radio-group
                      v-model="question.answer"
                      row
                      :height="10"
                      :rules="question.required ? requireRule : []"
                    >
                      <v-radio label="Yes" value="Yes"></v-radio>
                      <v-radio label="No" value="No"></v-radio>
                    </v-radio-group>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list-group>
          </v-list>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<style type="css" scoped>
#adaptiveGroup >>> .v-list__tile {
  padding-left: 5px;
}

.question {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: initial !important;
}
</style>
<script>
import GoogleMapPicker from '@/modules/loanpro/components/loan-pro/WorkLoad/GoogleMapPicker.vue';
import GeocodingService from '@/modules/global-settings/loan-origination/services/geocoding.service';
import pageMixin from '@/mixins/page.mixin';
import { mapGetters } from 'vuex';
import { isEmpty } from 'lodash';
import { v4 } from 'uuid';

export default {
  mixins: [pageMixin],
  components: {
    GoogleMapPicker
  },

  computed: {
    ...mapGetters('loanSettingsModule', ['yapuSettings']),
    ...mapGetters('yapuModule', ['yapuManagementSettings']),

    homeTypes() {
      // check if yapuManagementSettings.homeType is empty
      if (isEmpty(this.yapuManagementSettings.homeType)) {
        return [];
      }
      return this.yapuManagementSettings.homeType.filter((el) => el.required);
    },
    businessTypes() {
      // check if yapuManagementSettings.businessType is empty
      if (isEmpty(this.yapuManagementSettings.businessType)) {
        return [];
      }
      return this.yapuManagementSettings.businessType.filter(
        (el) => el.required
      );
    }
  },

  props: {
    /**
     * loan object
     */
    loan: {
      type: Object,
      default: () => ({ purposes: [] })
    }
  },

  data() {
    return {
      valid: false,
      timeout: null,
      questionFormValid: false,
      locationPickerDialog: false,
      dialog: false,
      activeTab: null,
      lookupInProgress: false,
      lookupAddress: '',
      lookupResults: {},
      coordinates: { lat: 18, lng: -77 },
      geocodingService: {},
      yapuService: {},
      loanProduct: '',
      refId: '',
      adaptiveCapacityErrors: {},
      questions: {},
      requireRule: [(v) => !!v || 'This is required'],
      latitudeRules: [
        (v) =>
          (v !== '' && Number(v) > -90 && Number(v) < 90) ||
          'Latitude must be between -90 and 90 degrees',
        (v) =>
          (v !== '' && v !== undefined && v.toString().length < 65) ||
          'Latitude must be at most 64 characters'
      ],
      longitudeRules: [
        (v) =>
          (v !== '' && Number(v) > -180 && Number(v) < 180) ||
          'Longitude must be between -180 and 180 degrees',
        (v) =>
          (v !== '' && v.toString().length < 65) ||
          'Longitude must be at most 64 characters'
      ],
      isGoogleAPIAvailable: true
    };
  },

  async created() {
    // check if googleAPIKey is available
    this.isGoogleAPIAvailable =
      this.yapuSettings && !!this.yapuSettings?.googleAPIKey?.trim();
    // Send geocoding service to map picker component
    this.geocodingService = new GeocodingService({ ...this.yapuSettings });

    this.questions.exposure ||= this.yapuManagementSettings.exposure;
    this.questions.sensitivity ||= this.yapuManagementSettings.sensitivity;

    let adaptiveCap = this.yapuManagementSettings.adaptiveCapacity;
    if (adaptiveCap && adaptiveCap.length > 0) {
      adaptiveCap.forEach((c, i) => {
        if (i === 0) c.active = true;
        else c.active = false;
        c.questions.forEach((q) => {
          q.answer = q.required ? '' : 'No';
        });
      });
    }

    this.questions.adaptiveCapacity ||= adaptiveCap;
  },

  beforeDestroy() {
    clearTimeout(this.timeout);
  },

  methods: {
    toSentenceCase(str) {
      if (!str) return '';
      const f = str[0].toUpperCase();
      return `${f}${str.substring(1)}`;
    },

    countCompletedQuestions(questions = []) {
      return questions.filter((el) => el.answer).length;
    },


    load(refId, savedQuestions = {}, coordinates = { lat: 18, lng: -77 }) {
      this.refId = refId;

      //load perviously stored coordinates
      this.coordinates = coordinates;

      //load previous answers
      if (savedQuestions && Object.keys(savedQuestions).length > 0)
        this.questions = savedQuestions;
    },

    openLocationPicker() {
      this.locationPickerDialog = true;

      if (!this.isGoogleAPIAvailable) {
        if (this.timeout) clearTimeout(this.timeout);

        this.timeout = setTimeout(() => (this.activeTab = 1), 200);
      }
    },

    async searchGeocodingAPI() {
      if (this.lookupInProgress || !this.lookupAddress?.trim()) return;

      this.lookupInProgress = true;
      this.lookupResults = await this.geocodingService.getLagAndLong({
        address: this.lookupAddress?.trim()
      });
      this.lookupInProgress = false;
      this.dialog = true;
    },

    acceptSearchResults() {
      const { lat, lng } = this.lookupResults.data;
      this.coordinates = { lat, lng };
      this.dialog = false;
    },

    bringSearchResultsToMap() {
      this.acceptSearchResults();
      this.activeTab = 0;
    },

    validate() {
      this.adaptiveCapacityErrors = {};

      let yapuFormValid = true;
      let yapuQuestionFormValid = true;
      if (!this.$refs.form.validate()) {
        yapuFormValid = false;
      }

      if (!this.$refs.questionForm.validate()) {
        if (
          this.questions.adaptiveCapacity &&
          this.questions.adaptiveCapacity.length > 0
        ) {
          for (const item of this.questions.adaptiveCapacity) {
            this.adaptiveCapacityErrors[`${item.keyName}`] = {};
            this.adaptiveCapacityErrors[`${item.keyName}`] =
              item.questions.some((el) => !el.answer);
          }
        }

        yapuQuestionFormValid = false;
      }

      if (!yapuFormValid || !yapuQuestionFormValid) {
        return {
          valid: false,
          message: 'Please ensure all required YAPU questions are answered.'
        };
      }

      return { valid: true, message: '' };
    },

    getAssessmentData() {
      if (!this.validate()) return false;

      const yapuObject = {
        coordinates: this.coordinates,
        yapuRequest: this.formatData(),
        questions: this.questions
      };

      //save yapu details here
      this.$emit('save', yapuObject);

      return yapuObject;
    },

    formatData() {
      let data = {
        uniqueIdentifier: this.refId || v4(),
        loanType: this.loan?.loanType,
        geolocation: `${this.coordinates.lat}, ${this.coordinates.lng}`
      };

      if (this.questions.selectedHomeType) {
        data['homeType'] = this.questions.selectedHomeType;
      }

      if (this.questions.selectedBusinessType) {
        data['businessType'] = this.questions.selectedBusinessType;
      }

      if (this.questions.exposure && this.questions.exposure.length > 0) {
        data['exposure'] = {};
        for (const item of this.questions.exposure)
          data['exposure'][`${item.keyName}`] = item.selected;
      }

      if (this.questions.sensitivity && this.questions.sensitivity.length > 0) {
        data['sensitivity'] = {};
        for (const item of this.questions.sensitivity) {
          data['sensitivity'][`${item.keyName}`] = {
            type: item.selectedSensitivityType,
            material: item.selectedMaterial
          };
        }
      }

      if (
        this.questions.adaptiveCapacity &&
        this.questions.adaptiveCapacity.length > 0
      ) {
        data['adaptiveCapacity'] = {};
        for (const item of this.questions.adaptiveCapacity) {
          data['adaptiveCapacity'][`${item.keyName}`] = {};
          for (const question of item.questions) {
            data['adaptiveCapacity'][`${item.keyName}`][`${question.keyName}`] =
              question.answer;
          }
        }
      }

      return data;
    }
  }
};
</script>
